import type { Locale } from "antd/lib/locale";
import enGB from "antd/locale/en_GB";
import nlNL from "antd/locale/nl_NL";
import "dayjs/locale/nl";

/**
 * EN-5191: antd ships its own copy of every string it renders itself — the date picker's
 * month and weekday names, "Today", the calendar footer, table pagination, the empty-table
 * text. None of those go through i18next, so they stayed English while the rest of the
 * portal switched, and no key we could add would reach them.
 *
 * Feeding this to ConfigProvider is the whole fix. It does NOT change the global dayjs
 * locale: rc-picker calls `dayjs().locale(...)` per lookup off the locale carried in the
 * antd bundle, so the portal's own date formatting — and, importantly, the LONG_DATE_TIME_FORMAT
 * strings the patient-list sorters parse back — stay English. See the note in dateFormatter.ts.
 *
 * `dayjs/locale/nl` has to be imported for that lookup to resolve; dayjs falls back to English
 * silently when a locale was never registered, which is exactly the failure this file exists
 * to prevent.
 */
const ANTD_LOCALES: Record<string, Locale> = {
  "en-GB": enGB,
  "nl-NL": nlNL,
};

export const DEFAULT_ANTD_LOCALE = enGB;

/**
 * The antd locale for an i18next language code, falling back to en-GB. A language the portal
 * has translations for but antd does not ship is a real possibility, and English chrome around
 * translated content is better than a crash.
 */
export function getAntdLocale(language: string | undefined): Locale {
  if (!language) return DEFAULT_ANTD_LOCALE;

  return ANTD_LOCALES[language] ?? DEFAULT_ANTD_LOCALE;
}
